import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ValidationErrors,
	Validators,
	FormsModule,
	ReactiveFormsModule
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { debounceTime, firstValueFrom, skip, Subscription } from 'rxjs';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatOptionModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@copo/mat-datepicker-new';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor, DecimalPipe } from '@angular/common';

import { ShipToDetails } from '../../../order/models/ship-to-details';
import { UNDERSCORE } from '../../../shared/utils/constant';
import { CallableOrderItem } from '../../models/ecall-off-model';
import { DialogSaveCommentComponent } from '../ecall-off-steps/dialog-save-comment/dialog-save-comment.component';
import { DimensionPipe } from '../../../shared/pipes/format-dimension.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { CopoDatepickerInputDirective } from '../../../shared/directives/date-picker.directive';
import { ECallOffService } from '../../services/ecall-off.service';

interface CallableOrderItemForm {
	calledVolume?: string;
	comment?: string;
	requestedDeliveryDateFrom?: Date;
	requestedDeliveryDateTo?: Date;
	unloadingReference?: string;
	shipTo?: ShipToDetails;
}

export interface CallableOrderItemChange extends CallableOrderItemForm {
	id: string;
	isError: boolean;
}

@Component({
	selector: 'copo-callable-order-item',
	templateUrl: './callable-order-item.component.html',
	styleUrl: './callable-order-item.component.css',
	imports: [
		NgIf,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,

		MatButtonModule,
		MatIconModule,

		MatTooltipModule,

		MatSelectModule,

		MatOptionModule,
		NgFor,
		CopoDatepickerInputDirective,
		MatDatepickerModule,
		MatRippleModule,

		MatMenuModule,
		MatDividerModule,
		DecimalPipe,
		DatePipe,
		DimensionPipe
	]
})
export class CallableOrderItemComponent implements OnInit, OnDestroy {
	@Input() editCalledVolume = false;
	@Input() editDateFrom = false;
	@Input() editDateTo = false;
	@Input() editUnloadingReference = false;
	@Input() showComment = false;
	@Input() showDates = false;
	@Input() stepSummary = false;
	@Input() showMenu = false;
	@Input() showUnloadingReference = false;
	@Input() showShipTo = false;
	@Input() shipToDetails?: ShipToDetails[];
	@Input() orderItem?: CallableOrderItem;
	@Output() readonly callableOrderItemChange = new EventEmitter<CallableOrderItemChange>(true);
	@Output() readonly callableOrderItemDelete = new EventEmitter<CallableOrderItem>(true);
	@Output() readonly callableOrderItemView = new EventEmitter<CallableOrderItem>(true);

	readonly UNDERSCORE = UNDERSCORE;
	readonly CURRENT_DATE = moment().toDate();

	formGroupSubscription: Subscription;
	formGroup: FormGroup;
	isLessThanBatchMinQty = false;
	minBatchQty: number | undefined;
	isInvalid = false;
	isShipTo = false;
	constructor(

		readonly dialog: MatDialog,
		private readonly ecallService: ECallOffService
	) {
		this.formGroup = new FormGroup({
			calledVolume: new FormControl<string | null>(
				'',
				[
					Validators.pattern('[0-9]*(.[0-9]+){0,1}'),
					Validators.min(0),
					this.maxValueValidator()
				]
			),
			comment: new FormControl(''),
			requestedDeliveryDateFrom: new FormControl(),
			requestedDeliveryDateTo: new FormControl(),
			unloadingReference: new FormControl(''),
			shipTo: new FormControl('')
		});
		if (this.orderItem?.shipTo?.shipToDescription) {
			this.isShipTo = true;
		}
		this.formGroupSubscription = this.formGroup.valueChanges
			.pipe(skip(1)) // skip ngOnInit patch event
			.pipe(debounceTime(500))
			.subscribe(value => {
				this.onFormChange(<CallableOrderItemForm> value);
			});
	}

	ngOnInit(): void {
		this.formGroup.patchValue({
			calledVolume: this.orderItem?.calledWeight?.toString() ?? this.orderItem?.qtyBatchGross?.toString(),
			comment: this.orderItem?.comment ?? '',
			requestedDeliveryDateFrom: this.orderItem?.requestedDeliveryDateFrom,
			requestedDeliveryDateTo: this.orderItem?.requestedDeliveryDateTo,
			unloadingReference: this.orderItem?.unloadingReference ?? '',
			shipTo: this.orderItem?.shipTo ?? ''
		});
	}

	ngOnDestroy(): void {
		this.formGroupSubscription.unsubscribe();
	}

	onDelete() {
		if (!this.orderItem?.id) {
			return;
		}
		this.callableOrderItemDelete.emit(this.orderItem);
	}

	onView() {
		if (!this.orderItem?.id) {
			return;
		}
		this.callableOrderItemView.emit(this.orderItem);
	}

	isSevenColumns() {
		const isComment = this.isCommentVisible();
		const isDates = this.isDatesVisible();
		const shipTo = this.isShipTo;
		return (isComment && !isDates && shipTo) || (!isComment && isDates && shipTo);
	}

	isSevenColumnsNoShipTo() {
		const isComment = this.isCommentVisible();
		const isDates = this.isDatesVisible();
		const shipTo = this.isShipTo;
		return (!isDates && !shipTo && isComment) || (!isComment && isDates && !shipTo);
	}

	isSevenColumnsStepSummaryNoShipTo() {
		const isComment = this.isCommentVisible();
		const isDates = this.isDatesVisible();
		const shipTo = this.isShipTo;
		return isDates && shipTo && !isComment && this.stepSummary;
	}

	isEightColumns() {
		const isComment = this.isCommentVisible();
		const isDates = this.isDatesVisible();
		const shipTo = this.isShipTo;
		return ((!isComment && isDates && shipTo)
			|| (isComment && !isDates && shipTo))
		&& this.stepSummary;
	}

	isEightColumnsStepSummary() {
		const isComment = this.isCommentVisible();
		const isDates = this.isDatesVisible();
		const shipTo = this.isShipTo;
		return (isComment && isDates && shipTo)
			&& this.stepSummary;
	}

	isFiveColumnsStepSummaryNoShipTos() {
		const isComment = this.isCommentVisible();
		const isDates = this.isDatesVisible();
		const shipTo = this.isShipTo;
		return ((!isComment && isDates && !shipTo)
			|| (isComment && !isDates && !shipTo)
			|| (!isComment && !isDates && shipTo && this.stepSummary))
		&& this.stepSummary;
	}

	isFiveColumnsStepSummaryShipTos() {
		const isComment = this.isCommentVisible();
		const isDates = this.isDatesVisible();
		const shipTo = this.isShipTo;
		return (!isComment && !isDates && shipTo && this.stepSummary)
			&& this.stepSummary;
	}

	isFourColumnsStepInfoNoShipTos() {
		const isDates = this.isDatesVisible();
		const shipTo = this.isShipTo;
		return !isDates && !shipTo && !this.stepSummary;
	}

	isFourColumnsStepSummaryNoShipTos() {
		const isDates = this.isDatesVisible();
		const shipTo = this.isShipTo;
		return !isDates && !shipTo && this.stepSummary;
	}

	isSixColumnsStepInfoNoShipTos() {
		const isDates = this.isDatesVisible();
		const shipTo = this.isShipTo;
		return isDates && !shipTo && !this.stepSummary;
	}

	isSixColumnsStepSummaryNoShipTos() {
		const isComment = this.isCommentVisible();
		const isDates = this.isDatesVisible();
		const shipTo = this.isShipTo;
		return isDates && !shipTo && isComment && this.stepSummary;
	}

	maxValueValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = Number.parseFloat(<string> control.value);
			if (Number.isNaN(value)) {
				return null;
			}
			// TO UPDATE !!!
			this.isInvalid = !this.orderItem?.qtyBatchGross || value > this.orderItem.qtyBatchGross;
			if (this.isInvalid) {
				return { max: { value, maxValue: this.orderItem?.qtyBatchGross ?? 0 } };
			}
			return null;
		};
	}

	getCustomerOrderNumber() {
		const baseValue = this.orderItem?.customerOrderNumber;
		if (baseValue) {
			if (this.orderItem?.customerOrderItemNumber) {
				return `${ baseValue }/${ this.orderItem.customerOrderItemNumber }`;
			}
			return baseValue;
		}
		return '';
	}

	getSupplierOrderNumber() {
		const baseValue = this.orderItem?.supplierOrderNumber;
		if (baseValue) {
			if (this.orderItem?.supplierOrderItemNumber) {
				return `${ baseValue }/${ this.orderItem.supplierOrderItemNumber }`;
			}
			return baseValue;
		}
		return '';
	}

	hasDate() {
		return this.orderItem?.requestedDeliveryDateFrom !== undefined
			|| this.orderItem?.requestedDeliveryDateTo !== undefined;
	}

	getCalledVolumeError() {
		// called volume specific case
		const calledVolumeControl = this.formGroup.get('calledVolume');
		if (calledVolumeControl?.errors?.['min']) {
			return {
				key: 'filter.is-not-a-positive-number',
				params: {}
			};
		}
		if (calledVolumeControl?.errors?.['max']) {
			const max = <{ value: number; maxValue?: number }>calledVolumeControl.errors['max'];
			return {
				key: 'ecall-off.max',
				params: {
					value: max.maxValue
				}
			};
		}
		if (calledVolumeControl?.errors?.['pattern']) {
			return {
				key: 'filter.is-not-a-number',
				params: {}
			};
		}
		return null;
	}

	getError() {
		// loop over all the controls
		for (const key of Object.keys(this.formGroup.controls)) {
			const controlErrors = this.formGroup.get(key)?.errors;
			if (controlErrors) {
				return 'filter.field.invalid';
			}
		}
		return null;
	}

	onFormChange(value?: CallableOrderItemForm) {
		if (!this.orderItem?.id || !value) {
			return;
		}
		if ((value.calledVolume && this.orderItem.minQuantity && this.orderItem.qtyBatchGross)
			&& (Number(value.calledVolume) < this.orderItem.minQuantity)
			&& (this.orderItem.qtyBatchGross > this.orderItem.minQuantity)) {
			this.isLessThanBatchMinQty = true;
			this.minBatchQty = this.orderItem.minQuantity;
		} else {
			this.isLessThanBatchMinQty = false;
		}
		const hasError = this.getCalledVolumeError() !== null || this.getError() !== null;
		const event: CallableOrderItemChange = {
			...value,
			id: this.orderItem.id,
			isError: hasError
		};
		this.callableOrderItemChange.emit(event);
	}

	onCalledVolumeReset() {
		this.formGroup.patchValue({
			calledVolume: ''
		});
	}

	getTooltipText(tooltipText: string) {
		if (tooltipText && tooltipText.length > 25) {
			const textArray = tooltipText.match(/.{1,25}/g);
			return textArray ? textArray.join('\n') : tooltipText;
		}
		return tooltipText;
	}

	async openCommentDialog() {
		const dialogRef = this.dialog.open(DialogSaveCommentComponent, {
			width: '60%',
			data: {
				value: <string> this.formGroup.get('comment')?.value
			}
		});
		const comment = <string> await firstValueFrom(dialogRef.afterClosed());
		this.formGroup.patchValue({ comment });
	}

	dateFilter = (d: Date | null): boolean => this.ecallService.dateFilter(d);

	getMinToDate() {
		const fromDate = <Date> this.formGroup.get('requestedDeliveryDateFrom')?.value;
		return this.ecallService.getMinToDate(fromDate);
	}

	getMaxFromDate() {
		const toDate = <Date> this.formGroup.get('requestedDeliveryDateTo')?.value;
		return this.ecallService.getMaxFromDate(toDate);
	}

	compareShipTo(shipTo1: ShipToDetails, shipTo2: string | ShipToDetails): boolean {
		if (typeof shipTo2 === 'object' && 'shipTo' in shipTo2) {
			return shipTo1.shipTo === shipTo2.shipTo;
		}
		if (typeof shipTo2 === 'string') {
			const normalizedShipTo2 = shipTo2.replace(/^0+/, '');
			return shipTo1.shipTo === normalizedShipTo2;
		}
		return false;
	}

	onShipToChange(newShipTo: ShipToDetails): void {
		if (!this.orderItem) {
			return;
		}
		if (!this.orderItem.originalShipTo) {
			this.orderItem.originalShipTo = this.orderItem.shipTo;
		}
		this.formGroup.patchValue({ shipTo: newShipTo });
		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		this.onFormChange(this.formGroup.value);
	}

	private isCommentVisible() {
		return this.showComment;
	}

	private isDatesVisible() {
		return	this.editDateFrom
			|| this.editDateTo
			|| (
				this.showDates
				&& (this.orderItem?.requestedDeliveryDateFrom || this.orderItem?.requestedDeliveryDateTo)
			);
	}
}
